import React, { useLayoutEffect, useState } from 'react';
import './ChargingStationPanel.scss';
import AhmIcon from 'components/Global/AhmIcon/AhmIcon';
import {
  IChargingStation,
  IListConnector,
  IStationConnector,
} from 'interfaces/chargingLocator';

interface IChargingStationPanel {
  stationData: IChargingStation;
  labels: {
    acuraEVApp: string;
    details: string;
    connectors: string;
    amenities: string;
    resultDisclaimer: string;
  };
  onClose: () => void;
}

const ChargingStationPanel = ({
  stationData,
  labels,
  onClose,
}: IChargingStationPanel): JSX.Element => {
  const [animate, setAnimate] = useState(false);
  const totalConnectors = stationData.attributes.connectors.reduce(
    (total, current) => total + (current.amount || 0),
    0
  );

  const stationDetails = [
    ...stationData.attributes.access,
    ...stationData.attributes.accessRestrictions,
    ...stationData.attributes.parking,
  ];

  const hideAndClose = () => {
    setAnimate(false);
    setTimeout(() => {
      onClose();
    }, 800);
  };

  const finalConnectors = (connectors: IStationConnector[]) => {
    var finalResult: IListConnector[] = [];
    connectors.forEach((element) => {
      finalResult = [...finalResult, ...element.listConnectors];
    });
    return finalResult;
  };

  useLayoutEffect(() => {
    setTimeout(() => {
      setAnimate(true);
    });
  }, []);

  return (
    <div
      className={`charging-station-panel custom-scroll-dark ${
        animate ? 'active' : ''
      }`}
    >
      <div className="charging-station-panel__mobile-holder">
        <div className="charging-station-panel__eyebrow" />
      </div>
      <div className="charging-station-panel__header">
        <h2 className="ahm-heading5 charging-station-panel__title">
          {stationData.name}
        </h2>
        <div className="charging-station-panel__network-container">
          <p className="ahm-label3 charging-station-panel__network-title">
            {stationData.network?.name !== undefined
              ? `${stationData.network?.name} Network`
              : 'Network Name Unavailable'}
          </p>
          {stationData.isAcuraEvApp && (
            <p className="ahm-label4 charging-station-panel__network-pill">
              {labels.acuraEVApp}
            </p>
          )}
        </div>
        <button
          className="charging-station-panel__close"
          onClick={hideAndClose}
        >
          <AhmIcon iconName="close" />
        </button>
      </div>
      <div className="charging-station-panel__body">
        {stationData.address && (
          <div className="charging-station-panel__section">
            <h3 className="ahm-label3 charging-station-panel__section-title">
              {labels.details}
            </h3>
            <div className="charging-station-panel__group-container">
              <div className="charging-station-panel__group-item">
                <AhmIcon
                  classes="charging-station-panel__icon"
                  iconName={'location'}
                />
                <p className="ahm-label3 charging-station-panel__item-text">
                  {stationData.address}
                </p>
              </div>
              {stationDetails.map((element) => (
                <div className="charging-station-panel__group-item">
                  <img
                    className="charging-station-panel__icon"
                    alt={`${element.name} icon`}
                    src={element.iconUrl}
                  />
                  <p className="ahm-label3 charging-station-panel__item-text">
                    {element.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        {totalConnectors > 0 && (
          <div className="charging-station-panel__section">
            <h3 className="ahm-label3 charging-station-panel__section-title">
              {`${labels.connectors} (${totalConnectors})`}
            </h3>
            <div className="charging-station-panel__group-container">
              {finalConnectors(stationData.attributes.connectors).map(
                (element) => (
                  <div className="charging-station-panel__group-item">
                    <img
                      className="charging-station-panel__icon"
                      alt={`${element.id} connector icon`}
                      src={element.iconUrl || ''}
                    />
                    <div>
                      <p className="ahm-label3 charging-station-panel__item-text">
                        {`${element.speed} kW • ${element.quantifier}`}
                      </p>
                      <p className="ahm-label4 charging-station-panel__white-text">
                        {`${
                          element.amount
                        } ${labels.connectors.toLocaleLowerCase()}`}
                      </p>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}
        {stationData.attributes.amenities.length > 0 && (
          <div className="charging-station-panel__section">
            <h3 className="ahm-label3 charging-station-panel__section-title">
              {labels.amenities}
            </h3>
            <div className="charging-station-panel__group-container">
              {stationData.attributes.amenities.map((element) => (
                <div className="charging-station-panel__group-item">
                  <img
                    className="charging-station-panel__icon"
                    alt={`${element.name} icon`}
                    src={element.iconUrl}
                  />
                  <p className="ahm-label3 charging-station-panel__item-text">
                    {element.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="charging-station-panel__disclaimer-container">
        <p className="ahm-label4 charging-station-panel__disclaimer">
          {labels.resultDisclaimer}
        </p>
      </div>
    </div>
  );
};

export default ChargingStationPanel;
